import { useQuery } from '@apollo/client/react'
import { useMemo } from 'react'

import { gql } from '../../../../../__generated__/graphql/catalog/gql'
import { COLLECTION_ITEM_PAGE_SIZE } from '../../../../../constants'
import { QueryStatus } from '../../../../../lib/hooks/types'
import { deriveMapFromList } from '../../../../../lib/utils/unorganized'
import { CollectionItemFilterOutput } from '../../../../../types/graphql/collection-view'

import type { SearchQuery } from '../../../../../__generated__/graphql/catalog/graphql'

export const SEARCH_PAGE_QUERY = gql(`
  query Search($after: String, $first: Int!, $query: String!, $selectedFilters: [SearchFilterInput!], $collectionHandle: String) {
    search(query: $query, selectedFilters: $selectedFilters) {
      suggestions {
        query
      }
      items(first: $first, after: $after) {
        nodes {
          productInfo {
            id
            group {
              ...CollectionItemVariantGroup
            }
          }
        }
        pageInfo {
          endCursor
          hasNextPage
        }
        numItems
        colors {
          name
          hexCodes
        }
        filters {
          displayType
          displayName
          key
          filterValues {
            value
            state
          }
        }
      }
    }
  }
`)

export type UseSearchPageOpts = {
  query: string
  selectedFilters: { key: string; values: string[] }[]
}

export type SearchItemVariantGroup = SearchQuery['search']['items']['nodes'][number]['productInfo']

export const useSearchPageQuery = ({ query, selectedFilters }: UseSearchPageOpts) => {
  const {
    data,
    fetchMore: fetchMoreSearchResults,
    previousData,
    loading,
    error,
    networkStatus,
  } = useQuery(SEARCH_PAGE_QUERY, {
    variables: { query, selectedFilters, first: COLLECTION_ITEM_PAGE_SIZE },
    notifyOnNetworkStatusChange: true,
  })

  const searchData = (data ?? previousData)?.search

  const status = useMemo<QueryStatus>(() => {
    if (loading) return 'pending'
    if (error) return 'rejected'
    return 'resolved'
  }, [error, loading])

  const colors = searchData?.items.colors ?? []
  const colorsByName = deriveMapFromList(colors, o => o.name)
  const searchNumItems = searchData?.items.numItems ?? 0
  const searchPageInfo = searchData?.items.pageInfo
  const searchVariantGroups = searchData?.items.nodes.map(node => node.productInfo.group) ?? []
  const searchSuggestions = searchData?.suggestions.map(suggestion => suggestion.query) ?? []

  const searchFilters: CollectionItemFilterOutput = (searchData?.items.filters ?? [])
    .filter(searchFilter => searchFilter.filterValues.length > 1)
    .map((searchFilter, index) => {
      return {
        __typename: 'ProductCatalogProductFilter',
        name: searchFilter.key,
        displayValue: searchFilter.displayName,
        values: searchFilter.filterValues.map(filterValue => {
          const matchingColor = colorsByName[filterValue.value]
          if (searchFilter.key.toLowerCase().includes('color') && matchingColor) {
            return {
              __typename: 'ProductCatalogProductColorFilterValue',
              state: filterValue.state,
              value: filterValue.value,
              displayValue: filterValue.value,
              sortOrder: index,
              color: {
                __typename: 'ProductCatalogColor',
                id: matchingColor.name,
                hexCodes: matchingColor.hexCodes,
                swatchImage: null,
              },
            }
          } else {
            return {
              __typename: 'ProductCatalogGenericProductFilterValue',
              state: filterValue.state,
              value: filterValue.value,
              displayValue: filterValue.value,
              sortOrder: index,
            }
          }
        }),
      }
    })

  return {
    fetchMoreSearchResults,
    searchFilters,
    searchNumItems,
    searchPageInfo,
    searchVariantGroups,
    searchSuggestions,
    status,
    fetchingMore: networkStatus === 3,
  }
}
